/**
 * CommercialDataReviewTable — renders Gate 21 commercial extraction review UI.
 *
 * Transposed table: criteria as rows, vendors as columns. Shows raw_value + status chip
 * per cell with optional evidence tooltip. Supports inline raw_value editing.
 *
 * Author: Raj Kumar (raj.kumar@accellor.com)
 */
import React, { useState } from "react";
import {
  Box,
  Paper,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import MarkdownRenderer from "../../MarkdownRenderer";

type CommercialExtractionValue = {
  raw_value: string | null;
  normalized_value: number | null;
  unit: string | null;
  currency: string | null;
  mapped_rank: number | null;
  evidence: string | null;
  status: "extracted" | "missing" | "ambiguous" | "needs_mapping";
};

type ModifyEntry = {
  vendor_name: string;
  criterion_id: string;
  raw_value: string | null;
  normalized_value: number | null;
  mapped_rank: number | null;
  currency: string | null;
};

type CommercialDataReviewTableProps = {
  vendorInputs: Record<string, Record<string, CommercialExtractionValue>>;
  criteriaMeta: Record<string, { name: string; scoring_type: string }>;
  needsReviewFlags: Record<string, string[]>;
  message: string;
  isSubmitting: boolean;
  hasApproved: boolean;
  onApproveWithEdits: (modifyArray: ModifyEntry[]) => void;
};

const STATUS_COLORS: Record<string, string> = {
  extracted: "#4caf50",
  missing: "#f44336",
  ambiguous: "#ff9800",
  needs_mapping: "#1976d2",
};

const STATUS_LABELS: Record<string, string> = {
  extracted: "Extracted",
  missing: "Missing",
  ambiguous: "Ambiguous",
  needs_mapping: "Needs Mapping",
};

export default function CommercialDataReviewTable({
  vendorInputs,
  criteriaMeta,
  needsReviewFlags,
  message,
  isSubmitting,
  hasApproved,
  onApproveWithEdits,
}: CommercialDataReviewTableProps): JSX.Element {
  const vendorNames = Object.keys(vendorInputs);
  // Derive criterion IDs from vendorInputs so rows render even when criteriaMeta is absent
  const criterionIds = Array.from(
    new Set(vendorNames.flatMap((v) => Object.keys(vendorInputs[v] || {})))
  );

  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});

  const cellKey = (vendor: string, criterionId: string) => `${vendor}::${criterionId}`;

  const handleCellClick = (vendor: string, criterionId: string, currentValue: string | null) => {
    if (hasApproved) return;
    const key = cellKey(vendor, criterionId);
    if (!Object.prototype.hasOwnProperty.call(editedValues, key)) {
      setEditedValues((prev) => ({ ...prev, [key]: currentValue || "" }));
    }
    setEditingCell(key);
  };

  const handleApprove = () => {
    const modifyArray: ModifyEntry[] = Object.entries(editedValues)
      .filter(([key, val]) => {
        const [vendor, criterionId] = key.split("::");
        return val !== (vendorInputs[vendor]?.[criterionId]?.raw_value || "");
      })
      .map(([key, val]) => {
        const [vendor_name, criterion_id] = key.split("::");
        const cell = vendorInputs[vendor_name]?.[criterion_id];
        return {
          vendor_name,
          criterion_id,
          raw_value: val,
          normalized_value: cell?.normalized_value ?? null,
          mapped_rank: cell?.mapped_rank ?? null,
          currency: cell?.currency ?? null,
        };
      });
    onApproveWithEdits(modifyArray);
  };

  const getButtonLabel = () => {
    if (hasApproved) return "Approved";
    if (isSubmitting) return "Approving...";
    return "Approve & Continue";
  };

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid #b2e5de",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0fbf9" }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#004141",
                  fontSize: "14px",
                  borderBottom: "2px solid #b2e5de",
                  minWidth: 180,
                }}
              >
                Criterion
              </TableCell>
              {vendorNames.map((vendor) => (
                <TableCell
                  key={vendor}
                  sx={{
                    fontWeight: 600,
                    color: "#004141",
                    fontSize: "14px",
                    borderBottom: "2px solid #b2e5de",
                    minWidth: 160,
                  }}
                >
                  {vendor}
                  {needsReviewFlags[vendor]?.length > 0 && (
                    <Chip
                      label={`${needsReviewFlags[vendor].length} flagged`}
                      size="small"
                      sx={{
                        ml: 1,
                        backgroundColor: "#fff3e0",
                        color: "#e65100",
                        fontWeight: 600,
                        fontSize: "11px",
                        height: "20px",
                      }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {criterionIds.map((criterionId) => (
              <TableRow
                key={criterionId}
                sx={{
                  "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                  "&:hover": { backgroundColor: "#f0fbf9" },
                }}
              >
                <TableCell
                  sx={{ fontSize: "14px", fontWeight: 500, color: "#2a312b" }}
                >
                  {criteriaMeta[criterionId]?.name || criterionId}
                </TableCell>
                {vendorNames.map((vendor) => {
                  const cell = vendorInputs[vendor]?.[criterionId];
                  const key = cellKey(vendor, criterionId);
                  const isEditing = editingCell === key;
                  const isEdited = Object.prototype.hasOwnProperty.call(editedValues, key);

                  if (!cell) {
                    return (
                      <TableCell key={vendor} sx={{ fontSize: "13px", color: "#9e9e9e" }}>
                        —
                      </TableCell>
                    );
                  }

                  const displayValue = isEdited ? editedValues[key] : cell.raw_value;

                  const valueNode = isEditing ? (
                    <TextField
                      value={editedValues[key] ?? ""}
                      onChange={(e) =>
                        setEditedValues((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      size="small"
                      variant="outlined"
                      sx={{ mb: 0.5, "& input": { fontSize: "13px", py: "4px" } }}
                    />
                  ) : (
                    <Box
                      sx={{
                        fontSize: "13px",
                        color: isEdited ? "#1976d2" : "#2a312b",
                        mb: 0.5,
                        cursor: hasApproved ? "default" : "pointer",
                        "&:hover": hasApproved ? {} : { textDecoration: "underline", color: "#1976d2" },
                      }}
                      onClick={() => handleCellClick(vendor, criterionId, cell.raw_value)}
                    >
                      {displayValue || "—"}
                    </Box>
                  );

                  const cellContent = (
                    <Box>
                      {valueNode}
                      <Chip
                        label={STATUS_LABELS[cell.status] ?? cell.status}
                        size="small"
                        sx={{
                          backgroundColor: STATUS_COLORS[cell.status] ?? "#9e9e9e",
                          color: "#ffffff",
                          fontWeight: 600,
                          fontSize: "10px",
                          height: "18px",
                        }}
                      />
                    </Box>
                  );

                  return (
                    <TableCell key={vendor}>
                      {cell.evidence && !isEditing ? (
                        <Tooltip title={cell.evidence} arrow placement="top">
                          <Box sx={{ cursor: "help" }}>{cellContent}</Box>
                        </Tooltip>
                      ) : (
                        cellContent
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1": { fontSize: "20px", fontWeight: 600, color: "#004141", mb: 2 },
              "& h2": { fontSize: "16px", fontWeight: 600, color: "#004141", mb: 1.5, mt: 2 },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& hr": { border: "none", borderTop: "1px solid #b2e5de", margin: "16px 0" },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={handleApprove}
          disabled={isSubmitting || hasApproved}
          sx={{
            backgroundColor: "#00a888",
            "&:hover": { backgroundColor: "#008f75" },
            "&.Mui-disabled": { backgroundColor: "#ccc", color: "#666" },
            textTransform: "none",
            fontWeight: 600,
            px: 4,
          }}
        >
          {getButtonLabel()}
        </Button>
      </Box>
    </>
  );
}
